import { Dialog as ADialog, useDialogContext } from '@ark-ui/solid/dialog';
import { IconX } from '@bbr/icons/x-outline';
import { createVisibilityObserver } from '@solid-primitives/intersection-observer';
import { children, createContext, createSignal, Show, splitProps, useContext } from 'solid-js';
import { Portal } from 'solid-js/web';
import { tv } from 'tailwind-variants';
import { Button } from '../forms/button.tsx';
import { Form } from '../forms/form.tsx';
import { Heading } from '../text/heading.tsx';
import type { Accessor, ComponentProps, JSXElement, ParentProps } from 'solid-js';
import type { VariantProps } from 'tailwind-variants';

export { useDialogContext } from '@ark-ui/solid/dialog';

export function Dialog(props: ComponentProps<typeof ADialog.Root>) {
	return (
		<ADialog.Root lazyMount unmountOnExit {...props}>
			{props.children}
		</ADialog.Root>
	);
}

Dialog.Trigger = function DialogTrigger(props: ComponentProps<typeof ADialog.Trigger>) {
	return <ADialog.Trigger {...props} />;
};

Dialog.ButtonTrigger = function DialogButtonTrigger(props: ComponentProps<typeof Button>) {
	return (
		<Dialog.Trigger
			asChild={(p) => (
				// @ts-expect-error too complex
				<Button {...p()} {...props} />
			)}
		/>
	);
};

const Context = createContext<{ root: Accessor<HTMLElement | undefined> }>({
	root: () => undefined,
});

type ContentProps = ComponentProps<typeof ADialog.Content> &
	VariantProps<typeof containerClass> &
	VariantProps<typeof placement> & {
		noBackdrop?: boolean;
	};

Dialog.Container = function DialogContainer(props: ContentProps) {
	const [internal, spread] = splitProps(props, ['children', 'width']);
	const [ref, setRef] = createSignal<HTMLElement>();

	return (
		<Portal>
			<ADialog.Positioner class={placement({ placement: props.placement })}>
				<Show when={!props.noBackdrop}>
					<ADialog.Backdrop class="pointer-events-auto fixed inset-0 z-40 bg-zinc-900/20 backdrop-blur-xs data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=open]:animate-in data-[state=open]:fade-in" />
				</Show>
				<ADialog.Content {...spread} ref={setRef} class={containerClass({ width: internal.width })}>
					<Show when={ref()}>
						{(ref) => <Context.Provider value={{ root: ref }}>{props.children}</Context.Provider>}
					</Show>
				</ADialog.Content>
			</ADialog.Positioner>
		</Portal>
	);
};

const containerClass = tv({
	base: 'pointer-events-auto fixed bottom-0 z-50 max-h-[97dvh] w-dvw max-w-full overflow-x-hidden overflow-y-auto overscroll-contain rounded-t-md border border-zinc-200 bg-white text-zinc-950 shadow-xl duration-150 outline-none data-[state=closed]:animate-out data-[state=closed]:fade-out data-[state=closed]:slide-out-to-bottom data-[state=open]:animate-in data-[state=open]:duration-200 data-[state=open]:fade-in data-[state=open]:slide-in-from-bottom sm:bottom-auto sm:my-auto sm:h-fit sm:max-h-[min(44rem,95dvh)] sm:w-full sm:rounded-lg sm:data-[state=closed]:slide-out-to-bottom-0 sm:data-[state=closed]:zoom-out-50 sm:data-[state=open]:slide-in-from-bottom-0 sm:data-[state=open]:zoom-in-50 dark:border-zinc-800 dark:bg-zinc-900 dark:text-zinc-50',
	variants: {
		width: {
			small: 'sm:max-w-xl',
			medium: 'sm:max-w-2xl',
			large: 'sm:max-w-4xl',
		},
	},
	defaultVariants: {
		width: 'small',
	},
});

const placement = tv({
	base: 'pointer-events-none fixed inset-0 z-40 flex',
	variants: {
		placement: {
			'bottom-start': 'sm:items-end sm:justify-start',
			'bottom-end': 'sm:items-end sm:justify-end',
			'bottom-stretch': 'sm:items-end sm:justify-stretch',
			'bottom-center': 'sm:items-end sm:justify-center',
			'center-start': 'sm:items-center sm:justify-start',
			'center-end': 'sm:items-center sm:justify-end',
			'center-stretch': 'sm:items-center sm:justify-stretch',
			'center-center': 'sm:items-center sm:justify-center',
			'top-start': 'sm:items-end sm:justify-start',
			'top-end': 'sm:items-end sm:justify-end',
			'top-stretch': 'sm:items-end sm:justify-stretch',
			'top-center': 'sm:items-end sm:justify-center',
		},
		padding: {
			none: '',
			sm: 'sm:p-4',
			md: 'sm:p-8',
			lg: 'sm:p-16',
		},
	},
	defaultVariants: {
		placement: 'center-center',
		padding: 'md',
	},
});

Dialog.CloseButtonTrigger = function DialogCloseButtonTrigger() {
	return (
		<div class="**:data-icon:size-6 **:data-icon:transition-transform **:data-icon:duration-300 **:data-icon:group-hover/button:rotate-90">
			<ADialog.CloseTrigger
				asChild={(p) => (
					// @ts-expect-error iconOnly too complex
					<Button
						{...p()}
						appearance="subtle"
						rounding="circle"
						tabindex={1}
						icon={IconX}
						iconPosition="only"
						iconLabel="Close"
					/>
				)}
			/>
		</div>
	);
};
Dialog.CloseTrigger = ADialog.CloseTrigger;

Dialog.Title = function DialogTitle(props: ComponentProps<typeof ADialog.Title> & { noClose?: boolean }) {
	const { root } = useContext(Context);
	const [ref, setRef] = createSignal<HTMLElement>();
	const observer = createVisibilityObserver({ root: root(), threshold: 1 });
	// oxlint-disable-next-line solid/reactivity
	const visible = observer(ref);

	return (
		<>
			<div ref={setRef} />
			<div
				class="sticky top-0 z-10 flex h-16 flex-row items-center justify-between gap-4 border-b bg-white/85 py-2 ps-3 pe-0 backdrop-blur-md transition-colors sm:ps-6 dark:bg-zinc-900/85"
				classList={{
					'border-zinc-100 dark:border-zinc-900': visible(),
					'border-zinc-200 dark:border-zinc-700': !visible(),
				}}
			>
				<ADialog.Title asChild={(p) => <Heading level="h1" size="h5" {...p()} />}>{props.children}</ADialog.Title>
				<Show when={!props.noClose}>
					<div class="ms-auto shrink pe-2">
						<Dialog.CloseButtonTrigger />
					</div>
				</Show>
			</div>
		</>
	);
};

type CloseRenderChildren = (handleClose: () => void) => JSXElement;

Dialog.Content = function DialogContent(props: { children: JSXElement | CloseRenderChildren }) {
	const ctx = useDialogContext();
	const resolved = children(
		() =>
			// @ts-expect-error this is technically not okay but it works fine
			props.children,
	);
	return (
		<div class="w-full self-start p-3 sm:px-6">
			{typeof resolved() === 'function'
				? // @ts-expect-error this is correct per the narrowing
					resolved()(() => ctx().setOpen(false))
				: resolved()}
		</div>
	);
};

Dialog.Form = (props: ComponentProps<typeof Form>) => <Form {...props} class="gap-y-0" />;
Dialog.FormContent = (props: ParentProps) => (
	<div class="flex size-full flex-col gap-6 self-start p-3 sm:px-6" {...props} />
);

Dialog.Actions = function DialogActions(props: { children: JSXElement | CloseRenderChildren }) {
	const ctx = useDialogContext();
	const resolved = children(
		() =>
			// @ts-expect-error this is technically not okay but it works fine
			props.children,
	);
	const { root } = useContext(Context);
	const [ref, setRef] = createSignal<HTMLElement>();
	const observer = createVisibilityObserver({ root: root(), threshold: 1 });
	// oxlint-disable-next-line solid/reactivity
	const visible = observer(ref);

	return (
		<>
			<div
				class="sticky bottom-0 z-10 flex flex-row-reverse flex-wrap items-stretch justify-start gap-4 border-t bg-white/85 p-3 backdrop-blur-md sm:items-center dark:bg-zinc-900/85"
				classList={{
					'border-zinc-100 dark:border-zinc-900': visible(),
					'border-zinc-200 dark:border-zinc-700': !visible(),
				}}
			>
				{typeof resolved() === 'function'
					? // @ts-expect-error this is correct per the narrowing
						resolved()(() => ctx().setOpen(false))
					: resolved()}
			</div>
			<div ref={setRef} />
		</>
	);
};

Dialog.Action = function DialogAction(props: ComponentProps<typeof Button>) {
	return <Button {...props} />;
};
